<template>
  <div class="admin-tab-content">
    <div class="tab-header">
      <h2>🎯 Gestión de Pre-Eurekers</h2>
      <p class="description">Visualiza y administra las entradas programadas del programa Pre-Eureker</p>
    </div>

    <!-- Edit Entry Section (only shown when editing) -->
    <div v-if="editingEntry" class="edit-section">
      <h3>{{ editingEntry.target_date || editingEntry.target_role ? 'Editar Configuración' : 'Configurar Rol Objetivo' }}</h3>

      <div class="user-info-box">
        <p><strong>Usuario:</strong> {{ editingEntry.username }} (UID: {{ editingEntry.uid }})</p>
        <p><strong>Email:</strong> {{ editingEntry.email }}</p>
      </div>

      <div class="form-grid">
        <div class="control-section">
          <label for="target-date">Fecha Objetivo:</label>
          <input
            type="date"
            id="target-date"
            v-model="formData.target_date"
            class="admin-input"
            :min="minDate"
          />
          <p class="help-text">Fecha en la que se asignará automáticamente el rol objetivo</p>
        </div>

        <div class="control-section">
          <label for="target-role">Rol Objetivo:</label>
          <select id="target-role" v-model="formData.target_role" class="admin-select">
            <option value="none">Ninguno (None)</option>
            <option value="bronze">Bronce (Bronze)</option>
            <option value="silver">Plata (Silver)</option>
            <option value="gold">Oro (Gold)</option>
            <option value="platinum">Platino (Platinum)</option>
            <option value="avanzado_off">Avanzado OFF (avanzado_off)</option>
          </select>
          <p class="help-text">Rol que se asignará al usuario en la fecha objetivo</p>
        </div>
      </div>

      <div class="action-section">
        <button
          @click="saveEntry"
          class="btn-primary"
          :disabled="saving || !isFormValid"
        >
          {{ saving ? 'Guardando...' : (editingEntry.target_date || editingEntry.target_role ? 'Actualizar Configuración' : 'Guardar Configuración') }}
        </button>
        <button
          @click="cancelEdit"
          class="btn-secondary"
        >
          Cancelar
        </button>
      </div>
    </div>

    <!-- Entries List Section -->
    <div class="entries-section">
      <div class="section-header">
        <h3>Usuarios Pre-Eureker</h3>
        <button @click="loadEntries" class="btn-secondary" :disabled="loading">
          {{ loading ? 'Actualizando...' : 'Actualizar Lista' }}
        </button>
      </div>

      <div v-if="entries.length > 0" class="table-wrapper">
        <table class="entries-table">
          <thead>
            <tr>
              <th>UID</th>
              <th>Usuario</th>
              <th>Email</th>
              <th>Fecha Objetivo</th>
              <th>Rol Objetivo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in entries"
              :key="entry.uid"
              :class="{ 'due-date': isDue(entry.target_date) }"
            >
              <td>{{ entry.uid }}</td>
              <td>{{ entry.username }}</td>
              <td>{{ entry.email }}</td>
              <td>
                <span v-if="entry.target_role === 'manual'">Manual</span>
                <span v-else-if="entry.target_date" :class="{ 'date-warning': isDue(entry.target_date) }">
                  {{ formatDate(entry.target_date) }}
                </span>
                <span v-else class="no-data-inline">Sin configurar</span>
              </td>
              <td>
                <span v-if="entry.target_role" :class="['role-badge', `role-${entry.target_role}`]">
                  {{ getRoleLabel(entry.target_role) }}
                </span>
                <span v-else class="no-data-inline">Sin configurar</span>
              </td>
              <td class="actions-cell">
                <button
                  @click="editEntry(entry)"
                  class="btn-edit"
                  :disabled="saving || deleting"
                >
                  {{ entry.target_date || entry.target_role ? 'Editar' : 'Configurar' }}
                </button>
                <button
                  v-if="entry.target_date || entry.target_role"
                  @click="deleteEntry(entry.uid)"
                  class="btn-danger"
                  :disabled="saving || deleting"
                >
                  Marcar Exento
                </button>
                <button
                  @click="removeFromProgram(entry.uid)"
                  class="btn-danger"
                  :disabled="saving || deleting"
                >
                  Quitar Rol
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-data">
        No hay usuarios con rol Pre-Eureker
      </div>
      
      <div class="table-explanation">
        <p>Esta tabla representa la fecha en la que se le eliminará el rol pre eurekers a cada usuario y que rol se le asignará en su lugar.</p>
        <p>En caso de ser <span class="highlight-danger">marcado como exento</span> no habrá programación y el rol se gestionará de manera manual.</p>
        <p>El botón <span class="highlight-danger">quitar rol</span> elimina el rol pre eurekers para el usuario al instante.</p>
      </div>
    </div>

    <div v-if="message" :class="['message-box', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PreEurekersTab',
  data() {
    return {
      entries: [],
      loading: false,
      saving: false,
      deleting: false,
      editingEntry: null,
      formData: {
        target_date: '',
        target_role: 'bronze'
      },
      message: '',
      messageType: ''
    };
  },
  computed: {
    minDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    isFormValid() {
      if (!this.formData.target_role) return false;
      return !!this.formData.target_date;
    }
  },
  mounted() {
    this.loadEntries();
  },
  methods: {
    async loadEntries() {
      this.loading = true;

      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/adminmenu/pre-eureker/get-all`);

        if (response.data.success) {
          this.entries = response.data.entries || [];
        }
      } catch (error) {
        console.error('Error loading pre-eureker entries:', error);
        this.showMessage('Error al cargar las entradas', 'error');
      } finally {
        this.loading = false;
      }
    },
    async saveEntry() {
      if (!this.isFormValid) {
        this.showMessage('Por favor complete todos los campos', 'error');
        return;
      }

      if (!this.editingEntry) {
        this.showMessage('No hay entrada para actualizar', 'error');
        return;
      }

      this.saving = true;
      this.message = '';

      // Determine if we need to create or update
      const hasExistingData = this.editingEntry.target_date || this.editingEntry.target_role;
      const endpoint = hasExistingData ? 'update' : 'create';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/pre-eureker/${endpoint}`,
          {
            uid: this.editingEntry.uid,
            target_date: this.formData.target_date,
            target_role: this.formData.target_role
          }
        );

        if (response.data.success) {
          this.showMessage(
            hasExistingData ? 'Entrada actualizada exitosamente' : 'Configuración creada exitosamente',
            'success'
          );
          this.resetForm();
          await this.loadEntries();
        } else {
          this.showMessage(response.data.message || 'Error al guardar', 'error');
        }
      } catch (error) {
        this.showMessage('Error al guardar la entrada', 'error');
      } finally {
        this.saving = false;
      }
    },
    async deleteEntry(uid) {
      if (!confirm('¿Asignar estado Manual (Exento) a este usuario?')) {
        return;
      }

      this.deleting = true;
      this.message = '';

      try {
        // Assign manual (exento) by updating the entry
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/pre-eureker/update`,
          { uid, target_role: 'manual' }
        );

        if (response.data.success) {
          this.showMessage('Usuario marcado como Manual (Exento)', 'success');
          await this.loadEntries();
        } else {
          this.showMessage(response.data.message || 'Error al asignar Manual', 'error');
        }
      } catch (error) {
        this.showMessage('Error al asignar Manual', 'error');
      } finally {
        this.deleting = false;
      }
    },
    async removeFromProgram(uid) {
      if (!confirm('¿Quitar del programa Pre-Eureker? Se eliminará la programación y el rol.')) {
        return;
      }

      this.deleting = true;
      this.message = '';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/pre-eureker/remove-program`,
          { uid }
        );

        if (response.data.success) {
          this.showMessage('Usuario quitado del programa Pre-Eureker', 'success');
          await this.loadEntries();
        } else {
          this.showMessage(response.data.message || 'Error al quitar del programa', 'error');
        }
      } catch (error) {
        this.showMessage('Error al quitar del programa', 'error');
      } finally {
        this.deleting = false;
      }
    },
    editEntry(entry) {
      this.editingEntry = entry;

      // Convert timestamp to date string if exists
      if (entry.target_date) {
        const date = new Date(entry.target_date * 1000);
        this.formData.target_date = date.toISOString().split('T')[0];
      } else {
        this.formData.target_date = '';
      }

      // Set role if exists, otherwise use default; if current is manual, default to bronze for editing
      this.formData.target_role = entry.target_role === 'manual' ? 'bronze' : (entry.target_role || 'bronze');

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    cancelEdit() {
      this.resetForm();
    },
    resetForm() {
      this.editingEntry = null;
      this.formData = {
        target_date: '',
        target_role: 'bronze'
      };
    },
    formatDate(timestamp) {
      if (!timestamp) return 'Sin configurar';
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    isDue(timestamp) {
      if (!timestamp) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(timestamp * 1000);
      targetDate.setHours(0, 0, 0, 0);
      return targetDate <= today;
    },
    getRoleLabel(role) {
      const labels = {
        none: 'Ninguno',
        bronze: 'Bronce',
        silver: 'Plata',
        gold: 'Oro',
        platinum: 'Platino',
        manual: 'Manual',
        avanzado_off: 'Avanz.OFF'
      };
      return labels[role] || role;
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type;

      if (type === 'success') {
        setTimeout(() => {
          this.message = '';
        }, 3000);
      }
    }
  }
};
</script>

<style scoped>
.admin-tab-content {
  padding: 20px;
}

.tab-header h2 {
  color: #3498db;
  font-size: 24px;
  margin-bottom: 10px;
}

.description {
  color: #bbb;
  margin-bottom: 30px;
}

.edit-section,
.entries-section {
  background: #2c2c2c;
  padding: 20px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.edit-section h3,
.entries-section h3 {
  color: #3498db;
  margin-bottom: 15px;
  font-size: 18px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.control-section {
  margin-bottom: 15px;
}

.control-section label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #e6e6e6;
}

.admin-input {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
  flex: 1;
}

.admin-select {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
  width: 100%;
}

.help-text {
  color: #aaa;
  font-size: 13px;
  margin-top: 5px;
}

.user-info-box {
  background: #333;
  padding: 15px;
  border-radius: 4px;
  border-left: 3px solid #3498db;
  margin-bottom: 20px;
}

.user-info-box p {
  margin: 5px 0;
  color: #e6e6e6;
}

.action-section {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.btn-primary,
.btn-secondary,
.btn-danger,
.btn-edit {
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  padding: 10px 20px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-primary {
  background: #3498db;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #95a5a6;
}

.btn-secondary:hover:not(:disabled) {
  background: #7f8c8d;
}

.btn-edit {
  background: #f39c12;
  padding: 6px 12px;
  font-size: 12px;
}

.btn-edit:hover:not(:disabled) {
  background: #e67e22;
}

.btn-danger {
  background: #e74c3c;
  padding: 6px 12px;
  font-size: 12px;
}

.btn-danger:hover:not(:disabled) {
  background: #c0392b;
}

.btn-primary:disabled,
.btn-secondary:disabled,
.btn-danger:disabled,
.btn-edit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.table-wrapper {
  overflow-x: auto;
}

.entries-table {
  width: 100%;
  border-collapse: collapse;
  background: #333;
  border-radius: 4px;
  overflow: hidden;
}

.entries-table th {
  background: #444;
  color: #e6e6e6;
  font-weight: 500;
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #555;
}

.entries-table td {
  padding: 10px;
  border-bottom: 1px solid #444;
  color: #e6e6e6;
}

.entries-table tr:hover {
  background: #3a3a3a;
}

.entries-table tr.due-date {
  background: rgba(231, 76, 60, 0.1);
}

.date-warning {
  color: #e74c3c;
  font-weight: 500;
}

.role-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.role-badge.role-none {
  background: #95a5a6;
  color: white;
}

.role-badge.role-bronze {
  background: #cd7f32;
  color: white;
}

.role-badge.role-silver {
  background: #c0c0c0;
  color: #333;
}

.role-badge.role-gold {
  background: #ffd700;
  color: #333;
}

.role-badge.role-platinum {
  background: #e5e4e2;
  color: #333;
}

.role-badge.role-manual {
  background: #8e44ad;
  color: #fff;
}

.role-badge.role-avanzado_off {
  background: #16a085;
  color: #fff;
}

.actions-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.no-data {
  text-align: center;
  padding: 30px;
  color: #bbb;
  font-style: italic;
  background: #333;
  border-radius: 4px;
}

.no-data-inline {
  color: #888;
  font-style: italic;
  font-size: 13px;
}

.message-box {
  background-color: #333;
  border-radius: 4px;
  margin-top: 15px;
  padding: 10px;
  border-left: 3px solid #3498db;
}

.message-box.success {
  border-left-color: #2ecc71;
  color: #2ecc71;
}

.message-box.error {
  border-left-color: #e74c3c;
  color: #e74c3c;
}

.table-explanation {
  margin-top: 20px;
  padding: 15px;
  background: #2c2c2c;
  border-radius: 4px;
  border-left: 3px solid #3498db;
}

.table-explanation p {
  margin: 8px 0;
  color: #bbb;
  font-size: 14px;
  line-height: 1.5;
}

.table-explanation p:first-child {
  margin-top: 0;
}

.table-explanation p:last-child {
  margin-bottom: 0;
}

.table-explanation .highlight-danger {
  color: #e74c3c;
  font-weight: 500;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .actions-cell {
    flex-direction: column;
  }
}
</style>
